import { Box, Typography } from '@mui/material'
import { Instagram, Users, Sparkles, ArrowRight } from 'lucide-react'
import React from 'react'

interface ModernTargetCardProps {
  icon: 'creators' | 'followers'
  title: string
  description: string
  navigation: () => void
  gradient: 'purple' | 'pink'
}

export default function ModernTargetCard({
  icon,
  title,
  description,
  navigation,
  gradient,
}: ModernTargetCardProps): JSX.Element {
  const gradients = {
    purple: {
      card: 'linear-gradient(135deg, rgba(168, 85, 247, 0.15) 0%, rgba(88, 28, 135, 0.15) 100%)',
      border: 'rgba(168, 85, 247, 0.3)',
      glow: 'rgba(168, 85, 247, 0.4)',
      iconBg: 'linear-gradient(135deg, #a855f7 0%, #9333ea 100%)',
      accent: '#a855f7',
    },
    pink: {
      card: 'linear-gradient(135deg, rgba(236, 72, 153, 0.15) 0%, rgba(219, 39, 119, 0.15) 100%)',
      border: 'rgba(236, 72, 153, 0.3)',
      glow: 'rgba(236, 72, 153, 0.4)',
      iconBg: 'linear-gradient(135deg, #ec4899 0%, #db2777 100%)',
      accent: '#ec4899',
    },
  }

  const colors = gradients[gradient]
  const IconComponent = icon === 'creators' ? Users : Sparkles

  return (
    <Box
      onClick={navigation}
      sx={{
        position: 'relative',
        background: colors.card,
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: `1px solid ${colors.border}`,
        padding: '40px',
        height: '400px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-12px)',
          border: `1px solid ${colors.accent}`,
          boxShadow: `0 20px 60px ${colors.glow}`,
          background: colors.card,
        },
        '&:hover .arrow-icon': {
          transform: 'translateX(8px)',
          opacity: 1,
        },
        '&:hover .main-icon': {
          transform: 'scale(1.1) rotate(5deg)',
        },
        '&:hover .decorative-circle': {
          transform: 'scale(1.5)',
          opacity: 0.3,
        },
      }}
    >
      {/* Decorative Background Circle */}
      <Box
        className="decorative-circle"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '200px',
          height: '200px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colors.accent}20 0%, transparent 70%)`,
          transition: 'all 0.4s ease',
          pointerEvents: 'none',
        }}
      />

      {/* Gradient Top Border */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: colors.iconBg,
        }}
      />

      {/* Main Icon */}
      <Box
        className="main-icon"
        sx={{
          width: '120px',
          height: '120px',
          borderRadius: '24px',
          background: colors.iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 4,
          boxShadow: `0 12px 40px ${colors.glow}`,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <IconComponent size={56} color="white" strokeWidth={1.5} />
      </Box>

      {/* Title */}
      <Typography
        sx={{
          fontSize: '28px',
          fontWeight: 700,
          color: 'rgba(255, 255, 255, 0.95)',
          mb: 2,
          textAlign: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {title}
      </Typography>

      {/* Description */}
      <Typography
        sx={{
          fontSize: '15px',
          color: 'rgba(255, 255, 255, 0.6)',
          textAlign: 'center',
          lineHeight: 1.6,
          mb: 3,
          maxWidth: '320px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {description}
      </Typography>

      {/* Platform Badge */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Instagram size={18} style={{ color: colors.accent }} />
        <Typography
          sx={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.8)',
            fontWeight: 600,
          }}
        >
          Instagram
        </Typography>
      </Box>

      {/* Arrow Icon (appears on hover) */}
      <Box
        className="arrow-icon"
        sx={{
          position: 'absolute',
          bottom: '24px',
          right: '24px',
          width: '40px',
          height: '40px',
          borderRadius: '12px',
          background: colors.iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: 0,
          transform: 'translateX(0)',
          transition: 'all 0.3s ease',
          boxShadow: `0 4px 20px ${colors.glow}`,
        }}
      >
        <ArrowRight size={20} color="white" strokeWidth={2.5} />
      </Box>

      {/* Corner Decorative Elements */}
      <Box
        sx={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '12px',
          border: `2px solid ${colors.border}`,
          opacity: 0.3,
          transform: 'rotate(45deg)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          border: `2px solid ${colors.border}`,
          opacity: 0.3,
          transform: 'rotate(30deg)',
          pointerEvents: 'none',
        }}
      />
    </Box>
  )
}